import { useEffect, useState } from "react";
import { useQueue } from "@/hooks/useQueue";
import { useDisplayQueue } from "@/hooks/useDisplayQueue";
import { useAnnunciatore } from "@/hooks/useAnnunciatore";
import { SALE, SALA_STYLE } from "@/lib/clinic";
import { Logo } from "@/components/Logo";
import { Wifi, WifiOff, Volume2, VolumeX, Megaphone } from "lucide-react";

const RoomPanel = ({ sala, patient, flash }) => {
  const s = SALA_STYLE[sala];
  return (
    <section className="flex flex-col gap-6" data-testid={`display-room-${sala}`}>
      <div className="flex items-center justify-between border-b border-white/10 pb-4">
        <div>
          <div className="font-display text-3xl lg:text-6xl font-bold uppercase tracking-[0.08em] text-white leading-none">
            {SALE[sala].label}
          </div>
          <div className="text-xs lg:text-base uppercase tracking-[0.3em] text-slate-400 mt-2">
            {SALE[sala].desc}
          </div>
        </div>
        <span className={`w-3 h-3 rounded-full ${s.dot} ${patient ? "animate-pulse" : "opacity-30"}`} />
      </div>

      {patient ? (
        <div
          key={`${patient.codice}-${flash}`}
          data-testid={`display-current-${sala}`}
          className={`flash-in call-pulse rounded-3xl border ${s.card} flex-1 flex flex-col justify-center py-14 lg:py-20 text-center backdrop-blur-sm`}
        >
          <div className="text-[10px] lg:text-sm uppercase tracking-[0.35em] text-emerald-300">
            Chiamata in corso
          </div>
          <div
            data-testid={`display-number-${sala}`}
            className={`font-ticket font-bold ${s.number} leading-none tracking-tighter text-[25vw] lg:text-[13.5rem] mt-4`}
          >
            {patient.codice}
          </div>
        </div>
      ) : (
        <div
          data-testid={`display-current-${sala}`}
          className="rounded-3xl border border-white/10 bg-white/[0.02] flex-1 flex flex-col justify-center py-14 lg:py-20 text-center"
        >
          <div className="text-[10px] lg:text-sm uppercase tracking-[0.35em] text-slate-500">Sala libera</div>
          <div className="font-ticket text-[21vw] lg:text-[11rem] text-white/10 leading-none">---</div>
        </div>
      )}
    </section>
  );
};

export default function Display() {
  const { current, connected, lastEvent } = useDisplayQueue();
  const { annuncia, provaVoce, enabled, setEnabled, voceDisponibile, needsUnlock, unlock } = useAnnunciatore();
  const [now, setNow] = useState(new Date());
  const [flash, setFlash] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (lastEvent?.type === "PATIENT_CALLED") {
      setFlash((f) => f + 1);
      const sala = lastEvent.payload?.sala;
      annuncia(lastEvent.payload?.codice, sala ? SALE[sala]?.label : "");
    }
  }, [lastEvent, annuncia]);

  return (
    <div className="min-h-screen bg-[#0A0F1D] text-white relative grain overflow-hidden">
      <div className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full bg-violet-500/10 blur-[130px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[460px] h-[460px] rounded-full bg-cyan-500/10 blur-[130px] pointer-events-none" />

      <div className="relative max-w-[1900px] mx-auto p-6 lg:p-10 min-h-screen flex flex-col gap-8">
        <header className="flex items-center justify-between gap-6 border-b border-white/10 pb-5">
          <div className="flex items-center gap-5 lg:gap-7">
            <Logo invert className="h-9 lg:h-14" />
            <div className="h-10 lg:h-14 w-px bg-white/15" />
            <div>
              <div className="font-display text-xl lg:text-2xl font-bold uppercase tracking-[0.18em]">
                Medicina del Lavoro
              </div>
              <div className="text-[10px] lg:text-xs uppercase tracking-[0.3em] text-slate-400 mt-1">
                Chiamata pazienti · Sala d'attesa
              </div>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <div className="text-right">
              <div className="font-ticket text-3xl lg:text-5xl font-bold tabular-nums">
                {now.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
              </div>
              <div className="text-[10px] lg:text-xs uppercase tracking-[0.22em] text-slate-400">
                {now.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
              </div>
            </div>
            <span data-testid="websocket-connection-status-badge" className={connected ? "text-emerald-400" : "text-rose-400"}>
              {connected ? <Wifi className="w-5 h-5" /> : <WifiOff className="w-5 h-5" />}
            </span>
            <button
              onClick={() => (needsUnlock ? unlock() : setEnabled((v) => !v))}
              data-testid="display-sound-toggle"
              title={enabled ? "Disattiva voce di chiamata" : "Attiva voce di chiamata"}
              className={`rounded-full border p-2.5 transition-colors ${
                needsUnlock
                  ? "border-amber-400/60 text-amber-300 bg-amber-400/10 animate-pulse"
                  : enabled
                  ? "border-white/15 text-slate-300 hover:text-white"
                  : "border-white/10 text-slate-600"
              }`}
            >
              {enabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            <button
              onClick={() => {
                unlock();
                provaVoce();
              }}
              data-testid="display-voice-test-btn"
              title="Prova la voce di chiamata"
              className="rounded-full border border-white/15 p-2.5 text-slate-300 hover:text-white transition-colors"
            >
              <Megaphone className="w-4 h-4" />
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 flex-1">
          {["levi", "jenner"].map((sala) => (
            <RoomPanel key={sala} sala={sala} patient={current?.[sala]} flash={flash} />
          ))}
        </div>

        <footer className="flex items-center justify-between gap-6 border-t border-white/10 pt-5 text-[10px] lg:text-sm uppercase tracking-[0.22em] text-slate-500">
          <span data-testid="display-footer-note">
            {needsUnlock
              ? "Tocca lo schermo per attivare la voce di chiamata"
              : !voceDisponibile
              ? "Voce italiana non disponibile su questo dispositivo · verrà usato il segnale acustico"
              : "Attendere la chiamata del proprio codice"}
          </span>
          <Logo invert className="h-5 lg:h-6 opacity-40" />
        </footer>
      </div>
    </div>
  );
}
